import { resolveRiceRecipePdfPath } from '@/utils/riceRecipePdfDownload';

/**
 * Нормализация ответа GET /api/quiz/won-rice и строк «рецептов» для UI.
 *
 * Доступность рецептов (поле available / status): won | next_available | not_available
 * Доступность цепочки казанов (корень ответа, опционально): available | all_won | not_available
 */

const RECIPE_AVAILABILITY = ['won', 'next_available', 'not_available'];

const CHAIN_ACCESS_KEYS = [
  'chain_access',
  'kazan_accessibility',
  'kazan_availability',
  'cauldron_availability',
  'cauldron_access',
  'accessibility',
];

const CHAIN_ACCESS_VALUES = ['available', 'all_won', 'not_available'];

/**
 * @param {unknown} avail
 * @returns {'won'|'next_available'|'not_available'}
 */
export function normalizeRecipeAvailability(avail) {
  const s = String(avail == null ? '' : avail).trim();
  if (RECIPE_AVAILABILITY.includes(s)) return s;
  return 'not_available';
}

/**
 * Извлекает статус цепочки казанов из корня JSON (если бэкенд отдаёт не только массив).
 * @returns {'available'|'all_won'|'not_available'}
 */
export function extractWonRiceChainAccess(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return 'available';
  }
  for (let i = 0; i < CHAIN_ACCESS_KEYS.length; i += 1) {
    const v = payload[CHAIN_ACCESS_KEYS[i]];
    if (v == null || String(v).trim() === '') continue;
    const s = String(v).trim();
    if (CHAIN_ACCESS_VALUES.includes(s)) return s;
  }
  return 'available';
}

/**
 * Массив игр из тела ответа (results / data / корневой массив).
 * @returns {unknown[]}
 */
export function wonRiceResponseToItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const arr = payload.results ?? payload.data ?? payload.items ?? payload.games;
  return Array.isArray(arr) ? arr : [];
}

/**
 * @returns {{ items: unknown[], chainAccess: 'available'|'all_won'|'not_available' }}
 */
export function parseWonRiceGamesResponse(payload) {
  return {
    items: wonRiceResponseToItems(payload),
    chainAccess: extractWonRiceChainAccess(payload),
  };
}

/**
 * Индекс казана в цепочке 0..5 из строки won-rice (если бэкенд отдаёт поле порядка).
 * Без известного поля — null (порядок строк не меняем).
 * @param {unknown} g
 * @returns {number|null}
 */
export function extractWonRiceChainSlotIndex(g) {
  if (!g || typeof g !== 'object') return null;
  const asSlot = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    if (n >= 1 && n <= 6) return Math.floor(n) - 1;
    if (n >= 0 && n <= 5) return Math.floor(n);
    return null;
  };
  const fromObj = (obj) => {
    if (!obj || typeof obj !== 'object') return null;
    const keys = [
      'slot_index',
      'slotIndex',
      'rice_slot',
      'riceSlot',
      'slot',
      'position',
      'order',
      'game_number',
      'gameNumber',
      'kazan_number',
      'kazanNumber',
      'chain_index',
      'chainIndex',
      'quiz_order',
      'sort_order',
      'sortOrder',
    ];
    for (let i = 0; i < keys.length; i += 1) {
      const t = asSlot(obj[keys[i]]);
      if (t != null) return t;
    }
    return null;
  };
  let s = fromObj(g);
  if (s != null) return s;
  if (g.quiz && typeof g.quiz === 'object') {
    s = fromObj(g.quiz);
    if (s != null) return s;
  }
  return null;
}

/**
 * Стабильная сортировка строк won-rice по слоту цепочки (слева направо = казаны 1–6).
 * @param {unknown[]} rows
 * @returns {unknown[]}
 */
function sortWonRiceSourceRowsByChainSlot(rows) {
  if (!Array.isArray(rows) || rows.length < 2) {
    return Array.isArray(rows) ? [...rows] : [];
  }
  const decorated = rows.map((row, origIdx) => ({
    row,
    origIdx,
    slot: extractWonRiceChainSlotIndex(row),
  }));
  if (!decorated.some((d) => d.slot != null)) {
    return [...rows];
  }
  return [...decorated]
    .sort((a, b) => {
      const as = a.slot != null ? a.slot : 1000 + a.origIdx;
      const bs = b.slot != null ? b.slot : 1000 + b.origIdx;
      if (as !== bs) return as - bs;
      return a.origIdx - b.origIdx;
    })
    .map((d) => d.row);
}

/**
 * Список из 6 карточек для сетки казанов (ЛК, игра, баннер).
 * @param {unknown[]} sourceRows
 */
export function normalizeWonRiceGamesListForUi(sourceRows) {
  let raw = Array.isArray(sourceRows) ? [...sourceRows] : [];
  raw = sortWonRiceSourceRowsByChainSlot(raw);
  while (raw.length < 6) {
    raw.push({
      title: `Казан ${raw.length + 1}`,
      available: 'not_available',
      image: '',
      receipt: '',
    });
  }
  return raw.slice(0, 6).map((g, i) => {
    const avail = g.available || g.status || g.recipe_status || 'not_available';
    const normalizedAvail = normalizeRecipeAvailability(avail);
    const playBlocked =
      g.is_playable === false ||
      g.play_available === false ||
      g.can_play === false ||
      g.game_available === false;
    return {
      title: (g.title && String(g.title).trim()) || `Рецепт ${i + 1}`,
      available: normalizedAvail,
      image: typeof g.image === 'string' ? g.image : '',
      receipt: resolveRiceRecipePdfPath(g),
      playBlocked,
      __raw: g,
    };
  });
}
